import { create } from 'zustand'
import { setAutoFreeze } from 'immer'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { pipe, Array as A, Option as O, Struct } from 'effect'
import { omit } from 'effect/Struct'
import { fromInteger } from '@repo/money'
import type { Bet } from 'src/types/domain/Bet'
import { betStatusMap } from 'src/types/domain/BetStatus'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'
import { getBetsByGameAndPlayer } from './utils/getBetsByGameAndPlayer'

setAutoFreeze(false)

export interface BetState {
  unacceptedBets: Record<string, Bet>
  bets: {
    byId: Record<string, Bet>
    allIds: string[]
  }
  /** Lightning config remembered per-round so bets that arrive AFTER the
   *  reveal (page-reload race condition) can be boosted on insert by the
   *  bet-arrival actions. Each entry is the data we need to compute the
   *  boost: which marble IDs are boosted and the boost percent. */
  lightningRoundConfigs: Record<string, { items: string[]; percent: number }>
  /** Set-like map of bet IDs that have already been boosted. Per-bet
   *  idempotency keeps the bulk apply safe to re-run (handleLightningRevealed
   *  + the useGameListeners backstop both call it) without double-boosting. */
  boostedBetIds: Record<string, true>
  /** The most recent bet rejection, kept so surfaces that need the REASON can
   *  read it (A88: a `RoundClosed` for a round Go never opened, told apart
   *  from the ordinary close race by how much of the closing window is left).
   *  `raceId` is the rejected bet's round, captured before the purge below —
   *  the rejection echo off the wire cannot be trusted for it. `detail`: what
   *  the rejection adds to its reason (`NotInTime`, a hard round's late answer). */
  lastRejection?: { betId: string; raceId?: string; reason: string; detail?: string; at: number }
}

export interface BetActions {
  betPlaced: (bet: Bet) => void
  betRejected: (betId: string, reason?: string, detail?: string) => void
  betAccepted: (bet: Bet) => void
  betUnaccepted: (bet: Bet) => void
  setBets: (bets: Bet[]) => void
  betWon: (bet: Bet) => void
  betLost: (bet: Bet) => void
  betCancelled: (bet: Bet) => void
  betVoided: (bet: Bet) => void
  betCashedOut: (betId: string, cashoutMultiplier: number, actualPayout: number) => void
  applyLightningBoost: (raceId: string, boostedItemIds: string[], boostPercent: number) => void
  getBetByRaceId: (raceId: string) => Bet | undefined
  getBetsByRaceId: (raceId: string) => Bet[]
}

type BetStore = BetState & { actions: BetActions }

export const useBetStore = create<BetStore>()(
  devtools(
    immer(
      subscribeWithSelector((set, get) => ({
        unacceptedBets: {},
        bets: {
          byId: {},
          allIds: []
        },
        lightningRoundConfigs: {},
        boostedBetIds: {},
        actions: {
          betPlaced: (bet: Bet) => {
            set(
              (state) => {
                state.bets.byId[bet.id] = bet

                if (!state.bets.allIds.includes(bet.id)) {
                  state.bets.allIds.push(bet.id)
                }

                // Wait for server response
                state.unacceptedBets[bet.id] = bet
              },
              undefined,
              { type: 'betPlaced', bet } as any
            )
          },
          betRejected: (betId: string, reason?: string, detail?: string) => {
            set(
              (state) => {
                // BEFORE the omit — the bet is about to be purged from both
                // maps and its raceId is the only reliable round identity we
                // have for the rejection.
                const tracked = state.unacceptedBets[betId] ?? state.bets.byId[betId]
                state.lastRejection = {
                  betId,
                  raceId: tracked?.raceId,
                  reason: reason ?? 'Unknown',
                  ...(detail ? { detail } : {}),
                  at: Date.now()
                }

                state.bets.byId = omit(state.bets.byId, betId)
                state.bets.allIds = state.bets.allIds.filter((id) => id !== betId)

                // Response received from server
                if (state.unacceptedBets[betId]) {
                  state.unacceptedBets = omit(state.unacceptedBets, betId)
                }
              },
              undefined,
              { type: 'betRejected', betId, reason, detail } as any
            )
          },
          setBets: (bets: Bet[]) => {
            set(
              (state) => {
                state.bets.byId = bets.reduce<Record<string, Bet>>((acc, bet) => {
                  acc[bet.id] = bet
                  return acc
                }, state.bets.byId)

                state.bets.allIds = bets.map((bet) => bet.id)
              },
              undefined,
              { type: 'setBets', bets } as any
            )
          },
          getBetByRaceId: (raceId: string) => {
            const state = get()
            const betId = state.bets.allIds.find((id) => state.bets.byId[id]?.raceId === raceId)
            return betId ? state.bets.byId[betId] : undefined
          },
          getBetsByRaceId: (raceId: string) => {
            const state = get()
            const betIds = state.bets.allIds.filter((id) => state.bets.byId[id]?.raceId === raceId)
            return betIds
              .map((id) => state.bets.byId[id])
              .filter((bet): bet is Bet => bet !== undefined)
          },
          betAccepted: (bet: Bet) => {
            set(
              (state) => {
                state.bets.byId[bet.id] = bet

                if (!state.bets.allIds.includes(bet.id)) {
                  state.bets.allIds.push(bet.id)
                }

                // Response received from server
                if (state.unacceptedBets[bet.id]) {
                  state.unacceptedBets = omit(state.unacceptedBets, bet.id)
                }

                // If the round already has a known lightning config (i.e.
                // reveal happened before this bet event landed in our
                // store — page-reload race condition), the bet:accepted
                // payload still carries the stripped pre-reveal value.
                // Apply the boost in-line so the local store matches
                // the persisted DDB value the operator will credit.
                const config = state.lightningRoundConfigs[bet.raceId]
                const stored = state.bets.byId[bet.id]
                if (
                  config &&
                  stored &&
                  bet.status === betStatusMap.PENDING &&
                  !state.boostedBetIds[bet.id]
                ) {
                  const onBoosted = bet.betDetails.selectedItems.some((m) =>
                    config.items.includes(m)
                  )
                  if (onBoosted) {
                    stored.potentialPayout = bet.potentialPayout * (1 + config.percent / 100)
                    state.boostedBetIds[bet.id] = true
                  }
                }
              },
              undefined,
              { type: 'betAccepted', bet } as any
            )
          },
          betUnaccepted: (bet: Bet) => {
            set(
              (state) => {
                state.bets.byId = omit(state.bets.byId, bet.id)
                state.bets.allIds = state.bets.allIds.filter((id) => id !== bet.id)
                state.unacceptedBets = omit(state.unacceptedBets, bet.id)
              },
              undefined,
              { type: 'betUnaccepted', bet } as any
            )
          },
          betWon: (bet: Bet) => {
            set(
              (state) => {
                const existing = state.bets.byId[bet.id]

                if (existing) {
                  state.bets.byId[bet.id] = {
                    ...existing,
                    status: betStatusMap.WON,
                    // For chip-placement lightning rounds the bet:accepted
                    // broadcast strips the boost out of `potentialPayout`
                    // (server-side mask, register_bet.go) so a sniffer can't
                    // see boosted marbles pre-reveal. The bet:won event
                    // carries the persisted (boosted) value from DDB —
                    // overwrite here so the bet history + win pill render
                    // the same number that was actually credited.
                    potentialPayout: bet.potentialPayout
                  }
                }
              },
              undefined,
              { type: 'betWon', bet } as any
            )
          },
          betLost: (bet: Bet) => {
            set(
              (state) => {
                const existing = state.bets.byId[bet.id]

                if (existing) {
                  state.bets.byId[bet.id] = {
                    ...existing,
                    status: betStatusMap.LOST
                  }
                }
              },
              undefined,
              { type: 'betLost', bet } as any
            )
          },
          betCancelled: (bet: Bet) => {
            set(
              (state) => {
                const existing = state.bets.byId[bet.id]

                if (existing) {
                  state.bets.byId[bet.id] = {
                    ...existing,
                    status: betStatusMap.CANCELLED
                  }
                }
              },
              undefined,
              { type: 'betCancelled', bet } as any
            )
          },
          betVoided: (bet: Bet) => {
            set(
              (state) => {
                const existing = state.bets.byId[bet.id]

                if (existing) {
                  state.bets.byId[bet.id] = {
                    ...existing,
                    status: betStatusMap.CANCELLED
                  }
                }
              },
              undefined,
              { type: 'betVoided', bet } as any
            )
          },
          betCashedOut: (betId: string, cashoutMultiplier: number, actualPayout: number) => {
            set(
              (state) => {
                const existing = state.bets.byId[betId]

                if (existing) {
                  // actualPayout is in cents, convert to decimal using the bet's currency
                  const payoutDecimal = fromInteger(actualPayout, existing.currency)
                  state.bets.byId[betId] = {
                    ...existing,
                    status: betStatusMap.CASHED_OUT,
                    cashoutMultiplier,
                    potentialPayout: payoutDecimal
                  }
                }
              },
              undefined,
              { type: 'betCashedOut', betId, cashoutMultiplier, actualPayout } as any
            )
          },
          // Reverses the chip-placement pre-reveal `potentialPayout` strip
          // (register_bet.go) the moment the lightning reveal fires. Bets
          // registered pre-reveal carry the unboosted payout in the local
          // store; multiplying by the lightning factor restores them to the
          // boosted value the backend actually persisted and will credit on
          // settle. We only touch pending bets — won/cashed-out bets get
          // their boosted value via bet:won / bet:cashed_out instead, and
          // double-applying would break the math.
          applyLightningBoost: (raceId: string, boostedItemIds: string[], boostPercent: number) => {
            if (!raceId || boostedItemIds.length === 0 || boostPercent <= 0) return
            const factor = 1 + boostPercent / 100
            const boostedSet = new Set(boostedItemIds)
            set(
              (s) => {
                // Remember the config so any bet that arrives AFTER this
                // call (page-reload case where lightning is already revealed
                // by the time the snapshot/live events land) can be boosted
                // on insert by `betAccepted`. Idempotent overwrite.
                s.lightningRoundConfigs[raceId] = {
                  items: boostedItemIds,
                  percent: boostPercent
                }
                for (const id of s.bets.allIds) {
                  const bet = s.bets.byId[id]
                  if (!bet) continue
                  if (bet.raceId !== raceId) continue
                  if (bet.status !== betStatusMap.PENDING) continue
                  if (s.boostedBetIds[id]) continue
                  const onBoosted = bet.betDetails.selectedItems.some((m) => boostedSet.has(m))
                  if (!onBoosted) continue
                  s.bets.byId[id] = {
                    ...bet,
                    potentialPayout: bet.potentialPayout * factor
                  }
                  s.boostedBetIds[id] = true
                }
              },
              undefined,
              {
                type: 'applyLightningBoost',
                raceId,
                boostedItemIds,
                boostPercent
              } as any
            )
          }
        }
      }))
    ),
    { name: 'BetStore', enabled: isDevtoolsEnabled() }
  )
)

export const useBetActions = () => useBetStore((state) => state.actions)

export const useGetBets = () =>
  useBetStore((state): Bet[] =>
    pipe(
      state.bets.allIds,
      A.map((id) => state.bets.byId[id]),
      A.map(O.fromNullable),
      A.getSomes
    )
  )

export const useGetBetsByGameAndPlayer = ({
  gameId,
  playerId
}: {
  gameId: string
  playerId: string
}) => useBetStore((state) => getBetsByGameAndPlayer(state, gameId, playerId))

export const useIsUnacceptedBet = (betId?: string) =>
  useBetStore((state): boolean => {
    if (!betId) {
      return false
    }

    return pipe(state.unacceptedBets, Struct.get(betId), O.fromNullable, O.isSome)
  })
